'use client';

import React, { useEffect, useState } from 'react';
import useInView from '../../../../helpers/UseInView';
import { useAOS } from '../../../../lib/hooks/useAOS';
import styles from './StatsSection.module.scss';

interface StatPercentageItem {
    label: string;
    count: number;
    percentage: number;
    link?: string;
}

interface StatsPercentageSectionProps {
    title: string;
    stats: StatPercentageItem[];
    theme?: 'light' | 'dark';
    disableAnimation?: boolean;
}

const StatsPercentageSection: React.FC<StatsPercentageSectionProps> = ({
    title,
    stats,
    theme = 'dark',
    disableAnimation = false,
}) => {
    useAOS();

    const [ref, isInView] = useInView();
    const [displayCounts, setDisplayCounts] = useState<number[]>(stats.map(() => 0));
    const [displayPercentages, setDisplayPercentages] = useState<number[]>(stats.map(() => 0)); // NEW STATE

    useEffect(() => {
        if (isInView || disableAnimation) {
            if (disableAnimation) {
                setDisplayCounts(stats.map(stat => stat.count));
                setDisplayPercentages(stats.map(stat => stat.percentage)); // SET FULL IMMEDIATELY
                return;
            }

            const countIntervals = stats.map((stat, index) => {
                const increment = Math.ceil(stat.count / 60);
                return setInterval(() => {
                    setDisplayCounts(prev => {
                        const newCounts = [...prev];
                        newCounts[index] = Math.min(stat.count, newCounts[index] + increment);
                        return newCounts;
                    });
                }, 30);
            });

            const percentIntervals = stats.map((stat, index) => {
                const increment = Math.max(stat.percentage / 60, 1); // At least 1 per step
                return setInterval(() => {
                    setDisplayPercentages(prev => {
                        const newPercents = [...prev];
                        newPercents[index] = Math.min(stat.percentage, newPercents[index] + increment);
                        return newPercents;
                    });
                }, 30);
            });

            return () => {
                countIntervals.forEach(clearInterval);
                percentIntervals.forEach(clearInterval);
            };
        }
    }, [isInView, stats]);

    return (
        <section
            className={`${styles.statsSection} ${theme === 'light' ? styles.light : styles.dark}`}
            ref={ref}
            data-aos='fade-up'
        >
            <h2 className={styles.title} data-aos='fade-right'>{title}</h2>
            <div className={styles.statsGrid}>
                {stats.map((stat, i) => (
                    <div key={i} className={styles.statBlock}>
                        <svg className={styles.circle} viewBox="0 0 36 36">
                            <path
                                className={styles.bg}
                                d="M18 2.0845
                                     a 15.9155 15.9155 0 0 1 0 31.831
                                     a 15.9155 15.9155 0 0 1 0 -31.831"
                            />
                            <path
                                className={styles.progress}
                                strokeDasharray={`${displayPercentages[i]}, 100`} // ANIMATED
                                d="M18 2.0845
                                     a 15.9155 15.9155 0 0 1 0 31.831
                                     a 15.9155 15.9155 0 0 1 0 -31.831"
                            />
                        </svg>
                        <div className={styles.percentageRow} data-aos='fade-down'>
                            <h4 className={styles.label} data-aos='fade'>
                                {stat.link ? <a href={stat.link}>{stat.label}</a> : stat.label}
                            </h4>
                            <h3 className={styles.count} data-aos='fade' data-aos-delay="100">{displayCounts[i].toLocaleString()}</h3>
                            <p className={styles.percentageText} data-aos='fade-down'>
                                {Math.round(displayPercentages[i])}% de notre base
                            </p>
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
};

export default StatsPercentageSection;
